<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useElementSize } from '@vueuse/core';
import {
  niceMax,
  gridValues,
  formatCount,
  formatPercent,
  percentOf,
} from '../../helpers/operationsChartHelper';

const props = defineProps({
  buckets: {
    type: Array,
    default: () => [],
  },
});

const { t, locale } = useI18n();

const plotRef = ref(null);
const { width } = useElementSize(plotRef);

const HEIGHT = 224;
const PADDING = { top: 20, right: 12, bottom: 40, left: 46 };

const chartWidth = computed(() => Math.max(width.value, 280));
const innerWidth = computed(
  () => chartWidth.value - PADDING.left - PADDING.right
);
const innerHeight = HEIGHT - PADDING.top - PADDING.bottom;

const total = computed(() =>
  props.buckets.reduce((sum, bucket) => sum + bucket.value, 0)
);
const breached = computed(() =>
  props.buckets
    .filter(bucket => bucket.breach)
    .reduce((sum, bucket) => sum + bucket.value, 0)
);

const max = computed(() =>
  niceMax(Math.max(1, ...props.buckets.map(bucket => bucket.value)))
);

const slot = computed(() => innerWidth.value / (props.buckets.length || 1));
const barWidth = computed(() => Math.min(46, slot.value - 10));

const grid = computed(() =>
  gridValues(max.value).map(value => ({
    label: formatCount(Math.round(value), locale.value),
    y: PADDING.top + innerHeight - (innerHeight * value) / max.value,
  }))
);

const bars = computed(() =>
  props.buckets.map((bucket, index) => {
    const height = (innerHeight * bucket.value) / max.value;
    const label = t(
      `OPERATIONS_REPORTS.SPREAD.BUCKETS.${bucket.bucket.toUpperCase()}`
    );
    return {
      key: bucket.bucket,
      x: PADDING.left + slot.value * index + (slot.value - barWidth.value) / 2,
      y: PADDING.top + innerHeight - height,
      height,
      breach: bucket.breach,
      label,
      labelX: PADDING.left + slot.value * index + slot.value / 2,
      tip: `${label}\n${t('OPERATIONS_REPORTS.SPREAD.TIP', {
        count: bucket.value,
        formattedCount: formatCount(bucket.value, locale.value),
        percent: formatPercent(
          percentOf(bucket.value, total.value),
          locale.value
        ),
      })}`,
    };
  })
);

// Everything to the right of this line waited longer than the hour the first-response SLA allows.
const breachLine = computed(() => {
  const index = props.buckets.findIndex(bucket => bucket.breach);
  return index < 0 ? null : PADDING.left + slot.value * index;
});
</script>

<template>
  <div
    class="flex flex-col p-4 border rounded-xl border-n-weak bg-n-solid-2 gap-1"
  >
    <div>
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('OPERATIONS_REPORTS.SPREAD.TITLE') }}
      </h3>
      <p class="mt-0.5 mb-0 text-xs text-n-slate-11">
        {{ t('OPERATIONS_REPORTS.SPREAD.SUBTITLE') }}
      </p>
    </div>

    <div ref="plotRef" class="w-full mt-2">
      <svg
        :viewBox="`0 0 ${chartWidth} ${HEIGHT}`"
        :height="HEIGHT"
        class="block w-full"
        role="img"
        :aria-label="t('OPERATIONS_REPORTS.SPREAD.TITLE')"
      >
        <g v-for="line in grid" :key="`grid-${line.y}`">
          <line
            :x1="PADDING.left"
            :y1="line.y"
            :x2="PADDING.left + innerWidth"
            :y2="line.y"
            class="stroke-n-weak"
            stroke-width="1"
          />
          <text
            :x="PADDING.left - 8"
            :y="line.y + 4"
            text-anchor="end"
            font-size="10"
            class="fill-n-slate-10 tabular-nums"
          >
            {{ line.label }}
          </text>
        </g>

        <template v-if="breachLine">
          <line
            :x1="breachLine"
            :y1="PADDING.top - 6"
            :x2="breachLine"
            :y2="PADDING.top + innerHeight"
            class="stroke-n-ruby-9"
            stroke-width="1"
          />
          <text
            :x="breachLine + 6"
            :y="PADDING.top - 8"
            font-size="10"
            class="fill-n-ruby-11"
          >
            {{ t('OPERATIONS_REPORTS.SPREAD.SLA_MARK') }}
          </text>
        </template>

        <g v-for="bar in bars" :key="bar.key">
          <rect
            :x="bar.x"
            :y="bar.y"
            :width="barWidth"
            :height="Math.max(bar.height, 0)"
            rx="4"
            :class="bar.breach ? 'fill-n-ruby-9' : 'fill-n-blue-9'"
            :data-tip="bar.tip"
          />
          <text
            :x="bar.labelX"
            :y="HEIGHT - 22"
            text-anchor="middle"
            font-size="10"
            class="fill-n-slate-10"
          >
            {{ bar.label }}
          </text>
        </g>

        <text
          :x="PADDING.left"
          :y="HEIGHT - 6"
          font-size="10"
          class="fill-n-slate-10"
        >
          {{
            t('OPERATIONS_REPORTS.SPREAD.FOOTNOTE', {
              count: breached,
              formattedCount: formatCount(breached, locale),
              percent: formatPercent(percentOf(breached, total), locale),
            })
          }}
        </text>
      </svg>
    </div>
  </div>
</template>
